/**
 * Utilitaire de calcul des frais Stripe pour répercuter les coûts sur le client
 * 
 * Frais Stripe applicables (2025) :
 * - Cartes européennes : 1,5% + 0,25€
 * 
 * On utilise le taux pour cartes européennes (1,5% + 0,25€)
 */

export interface StripeFeesBreakdown {
  baseAmount: number;        // Montant de base (valeur du bon cadeau)
  stripeFees: number;         // Frais Stripe calculés
  totalAmount: number;        // Montant total à payer par le client
  stripeFeesPercentage: number; // Pourcentage des frais (pour affichage)
}

/**
 * Calcule le montant total incluant les frais Stripe
 * 
 * Formule inversée pour que le vendeur reçoive exactement le montant de base :
 * total_client = (montant_base + frais_fixes) / (1 - pourcentage_stripe)
 * 
 * @param baseAmount - Montant de base en euros (valeur du bon cadeau)
 * @returns Détails de la répartition des frais
 */
export const calculateAmountWithStripeFees = (baseAmount: number): StripeFeesBreakdown => {
  // Frais Stripe pour cartes européennes
  const STRIPE_PERCENTAGE = 0.015; // 1,5%
  const STRIPE_FIXED_FEE = 0.25;   // 0,25€
  
  // Calcul du montant total que le client doit payer
  // On inverse la formule : si client paie X, vendeur reçoit X * (1 - 0.029) - 0.25
  // Donc si vendeur veut recevoir Y, client doit payer : (Y + 0.25) / (1 - 0.029)
  const totalAmount = (baseAmount + STRIPE_FIXED_FEE) / (1 - STRIPE_PERCENTAGE);
  
  // Calcul des frais Stripe réels
  const stripeFees = totalAmount - baseAmount;
  
  return {
    baseAmount: Math.round(baseAmount * 100) / 100,     // Arrondi à 2 décimales
    stripeFees: Math.round(stripeFees * 100) / 100,     // Arrondi à 2 décimales
    totalAmount: Math.round(totalAmount * 100) / 100,   // Arrondi à 2 décimales
    stripeFeesPercentage: STRIPE_PERCENTAGE * 100
  };
};

/**
 * Formate l'affichage des frais pour le client
 * 
 * @param baseAmount - Montant de base en euros
 * @returns Texte formaté avec la répartition complète
 */
export const formatFeesBreakdown = (baseAmount: number): string => {
  const breakdown = calculateAmountWithStripeFees(baseAmount);
  return `Montant du bon cadeau : ${breakdown.baseAmount.toFixed(2)}€
Frais de traitement : ${breakdown.stripeFees.toFixed(2)}€
Total à payer : ${breakdown.totalAmount.toFixed(2)}€`;
};

/**
 * Calcule uniquement le montant total (pour utilisation simplifiée)
 * 
 * @param baseAmount - Montant de base en euros
 * @returns Montant total à payer par le client
 */
export const calculateTotalWithFees = (baseAmount: number): number => {
  const breakdown = calculateAmountWithStripeFees(baseAmount);
  return breakdown.totalAmount;
};
